/**
 * **The carrier-self save/check fold and roller.** Every save or check a combatant rolls for
 * itself reads the same standing modifiers: its statuses (Power Up's +1, Bolster's +2 then
 * consumed, Impaired's -1, Stunned's HULL/AGILITY auto-fail), its frozen pilot effects (`t_ace:1`
 * while Flying, `t_bonded:1` while adjacent to its Bondmate), its equipped systems (Stable
 * Structure, cause-scoped), and boons an adjacent ally confers (the Bondmate's half of Bonded).
 * {@link gatherSelfSaveCheckModifiers} is that gather; {@link foldSelfSaveCheck} scopes it by the
 * roll's stat and cause and folds it.
 *
 * {@link rollSelfCheck} is the roller for the engine's hard-coded self-rolls (the structure
 * table's HULL check, the overheating table's ENGINEERING check, the burn tick, dangerous
 * terrain, mine saves and SYSTEMS checks, the meltdown AGILITY save): the d20 plus the folded
 * accuracy dice come from the caller's dice source, so the draws record and replay like any other.
 */

import { resourceCapKey, resourceCapRawKey, type Combatant, type EncounterState, type ResolvedStep, type SaveStep } from "@lancer-vtt/domain";
import { rollD20, type DiceSource } from "../dice";
import { attackPredicateContext, foldModifiers, scopeSaveModifiers, type BoundModifier, type ModifierContributionSum, type SaveScopeContext } from "./effects/modifiers";
import { seedModifiers, seedPilotModifiers, seedSystemModifiers, SEED_OVERLAY } from "./effects/seed";
import { NeedsDecisionSignal, type ChoiceSource } from "./effects/choices";
import { combatantEffectIds, gatherConferredModifiers, gatherReplacements, type BoundReplacement, type LoadedOverlay } from "./effects/loader";
import type { CheckReplacementEffect, SaveCheckRoll } from "./effects/atoms";
import { bondmatePartner } from "./effects/predicate";
import { capUseSteps, replacementOrder, spendStepsNow } from "./events/gameEvents";
import { createEventLoop, type EventLoop, type Replacement } from "./events/loop";
import { drawRoll, type CheckEvent } from "./events/rollEvents";

/**
 * What a self-check needs to fold the roller's modifiers: the live state (statuses, positions for
 * Bonded's adjacency), the room's overlay (pack statuses and talents; `SEED_OVERLAY` carries
 * neither Power Up nor any talent), and the statuses already consumed by an earlier roll in the
 * same resolution (a Bolster spent on the burn check must not ride the HULL check its damage
 * then raises). Build one with {@link selfCheckContext} per resolution and thread it through.
 */
export type SelfCheckContext = {
  state: EncounterState;
  overlay?: LoadedOverlay;
  /** How a reroll offer ({@link settleCheck}) is answered; absent, nothing is offered. */
  choices?: ChoiceSource;
  /**
   * `${carrierId}\u0000${statusName}` for every `consumeOnSaveCheck` status spent in this
   * resolution, and `${carrierId}\u0000${capKey}` for every cap counter a roll spent.
   */
  spent: Set<string>;
  /** The intent's event loop: each roll is raised into it as a `roll` event. Absent, the roll draws with no event. */
  loop?: EventLoop;
};

export function selfCheckContext(state: EncounterState, overlay: LoadedOverlay | undefined, opts: { choices?: ChoiceSource; loop?: EventLoop | undefined } = {}): SelfCheckContext {
  const { choices, loop } = opts;
  return { state, ...(overlay !== undefined ? { overlay } : {}), ...(choices !== undefined ? { choices } : {}), spent: new Set(), ...(loop !== undefined ? { loop } : {}) };
}

/** The prompt id a reroll offer asks under (`event-engine-to-hit` R6): source-qualified, so two sources never share one. `stat` is the stat rolled, or `"overheating"`. */
export function rerollPromptId(args: { source: string; roller: string; stat: string }): string {
  return `reroll:${args.source}:${args.roller}:${args.stat}`;
}

/** What a check's roll settled, for {@link settleCheck}: the roll's event id, its total, and (a save or check) whether it failed. */
export type CheckRoll<R> = { roll: R; id?: string; total: number; failed?: boolean };

/** Named args for {@link settleCheck}. */
export type SettleCheckArgs<R> = {
  ctx: SelfCheckContext | undefined;
  dice: DiceSource;
  roller: Combatant;
  purpose: CheckEvent["purpose"];
  /** The DC a save or check is rolled against; absent for the overheating pool. */
  dc?: number;
  first: CheckRoll<R>;
  /** Draw the roll again, the same way, for a reroll whose result stands. */
  reroll: () => CheckRoll<R>;
  /** What the offer says about the roll in front of the roller ("HULL save failed (8 vs 10)"). */
  label: (current: CheckRoll<R>) => string;
};

/**
 * **A check event and its reroll replacements** (`event-engine-to-hit` 2.2, R6, R7). Raises the
 * `check` for `first`, offered to the roller's own `on: "check"` replacement clauses in the F2a
 * order (the Student-Class NHP on a failed save or check, Reactor Stabilizer on any overheating
 * roll). Each asks keep-or-reroll under its own {@link rerollPromptId}; interactive and
 * unanswered, it halts. A reroll draws again and the new roll stands, so a later source is offered
 * the new one. A `perRound` source is offered only while its round's use is unspent, and spends it.
 * Nothing is offered without `ctx.choices`. Returns the roll that stands and the spend steps, which
 * the caller records after the roll's own step ([save, spend], R7).
 */
export function settleCheck<R>(args: SettleCheckArgs<R>): { standing: CheckRoll<R>; spend: ResolvedStep[] } {
  const { ctx, dice, roller, purpose, dc, first, reroll, label } = args;
  let standing = first;
  const spend: ResolvedStep[] = [];
  const check: CheckEvent = { kind: "check", roller: roller.id, purpose, ...(first.id !== undefined ? { roll: first.id } : {}), total: first.total, ...(dc !== undefined ? { dc } : {}), ...(first.failed !== undefined ? { verdict: first.failed ? "fail" : "pass" } : {}) };
  const sources = ctx?.choices === undefined ? [] : checkReplacements(ctx, roller.id, purpose.kind === "overheating" ? "overheating" : "save_check");
  const replacements: Replacement[] = sources.map((r) => {
    const stat = purpose.kind === "overheating" ? "overheating" : purpose.stat;
    const promptId = rerollPromptId({ source: r.entityId, roller: roller.id, stat });
    return {
      id: promptId,
      replace: (event) => {
        if (event.kind !== "check" || standing.failed === false) return event;
        const capKey = resourceCapKey(r.entityId);
        if (r.clause.instead.perRound === true && ((ctx!.state.combatants[roller.id]?.current.resources?.[capKey] ?? 0) >= 1 || ctx!.spent.has(spentKey(roller.id, capKey)))) return event;
        const answer = ctx!.choices!.choose(promptId, ["keep", "reroll"]);
        if (answer === undefined && ctx!.choices!.requireAnswer) {
          throw new NeedsDecisionSignal(promptId, `${roller.name}: ${label(standing)} — reroll it? (${r.entityId}; the new roll stands)`, {
            options: [
              { id: "keep", label: "Keep" },
              { id: "reroll", label: "Reroll" },
            ],
            who: roller.id,
          });
        }
        if (answer !== "reroll") return event;
        if (r.clause.instead.perRound === true) {
          ctx!.spent.add(spentKey(roller.id, capKey));
          spend.push(...spendStepsNow([{ kind: "counter", carrier: roller.id, key: capKey, delta: 1 }], ctx!.state, ctx!.loop));
        }
        standing = reroll();
        return { ...event, ...(standing.id !== undefined ? { roll: standing.id } : {}), total: standing.total, ...(standing.failed !== undefined ? { verdict: standing.failed ? "fail" : "pass" } : {}) };
      },
    };
  });
  (ctx?.loop ?? createEventLoop({ dice })).raise(check, { replacements, trigger: () => undefined });
  return { standing, spend };
}

/** The roller's own check replacements of one kind, in the F2a order. */
function checkReplacements(ctx: SelfCheckContext, rollerId: string, kind: CheckReplacementEffect["check"]): BoundReplacement<CheckReplacementEffect>[] {
  const own = gatherReplacements(ctx.state, ctx.overlay ?? SEED_OVERLAY).filter((r): r is BoundReplacement<CheckReplacementEffect> => r.carrier === rollerId && r.clause.on === "check" && r.clause.check === kind);
  return replacementOrder(own, rollerId, ctx.state);
}

/** Every carrier-self save/check modifier source for `carrierId`, unscoped. */
export function gatherSelfSaveCheckModifiers(state: EncounterState, carrierId: string, overlay: LoadedOverlay = SEED_OVERLAY): BoundModifier[] {
  const c = state.combatants[carrierId];
  return [
    ...seedModifiers(state, carrierId, overlay),
    ...seedPilotModifiers({
      carrierId,
      pilotEffectIds: combatantEffectIds(c),
      overlay,
      // The carrier's bound Bondmate, so a clause gated on `adjacent_to(self, partner)` resolves.
      partner: bondmatePartner(state, carrierId),
    }),
    ...seedSystemModifiers(state, carrierId, overlay),
    // An adjacent ally may confer a save/check boon (the Bondmate's half of `t_bonded:1`).
    ...gatherConferredModifiers(state, carrierId, overlay),
  ];
}

/** Named args for {@link foldSelfSaveCheck}. */
export type FoldSelfSaveCheckArgs = {
  state: EncounterState;
  carrierId: string;
  overlay?: LoadedOverlay;
  /** The roll's stat and cause; a scoped modifier only counts a dimension it authors and this names. */
  scope: SaveScopeContext;
  /** Statuses and caps already spent in this resolution (see {@link SelfCheckContext.spent}); their modifiers are dropped. */
  spent?: ReadonlySet<string>;
};

export function foldSelfSaveCheck(args: FoldSelfSaveCheckArgs): ModifierContributionSum {
  const { state, carrierId, overlay, scope, spent } = args;
  const gathered = gatherSelfSaveCheckModifiers(state, carrierId, overlay).filter((m) => {
    if (spent === undefined) return true;
    const cap = m.effect.mod.consumeResourceOnApply;
    return !spent.has(spentKey(m.carrier, m.name)) && (cap === undefined || !spent.has(spentKey(m.carrier, resourceCapKey(resourceCapRawKey(cap)))));
  });
  return foldModifiers(
    scopeSaveModifiers(gathered, scope),
    "attacker", // side label is irrelevant for the carrier-self save/check dimension
    attackPredicateContext({ state, attacker: carrierId, target: carrierId }),
  );
}

function spentKey(carrierId: string, status: string): string {
  return `${carrierId}\u0000${status}`;
}

/**
 * The cap uses a save or check spends (BC-011): every capped modifier its fold counted, settled
 * through the loop as one `resource_delta +1` each, after the roll. Each is recorded in `spent`, so
 * a later roll in the same resolution folds without it. Only a rolled save or check spends: an
 * auto-fail or a concession uses no modifier, the same reading as Bolster's.
 */
/**
 * The consume-on-use statuses a boosted roll spends (Bolster, Core p.69: "+2 Accuracy on the next
 * skill check or save"), as `consume_status` costs settled where the roll stands. Each carries
 * its instance's facets, so the remove's inverse re-adds the exact instance on undo.
 */
export function spendConsumedStatuses(args: { carrier: Pick<Combatant, "id" | "statuses">; statuses: readonly string[]; state: EncounterState; loop?: EventLoop | undefined }): ResolvedStep[] {
  const { carrier, statuses, state, loop } = args;
  return spendStepsNow(
    statuses.map((status) => {
      const inst = carrier.statuses.find((s) => s.name === status);
      const facets = { ...(inst?.expiry !== undefined ? { expiry: inst.expiry } : {}), ...(inst?.appliedTurn !== undefined ? { appliedTurn: inst.appliedTurn } : {}), ...(inst?.source !== undefined ? { source: inst.source } : {}) };
      return { kind: "consume_status" as const, carrier: carrier.id, status, facets };
    }),
    state,
    loop,
  );
}

export function spendFoldedCaps(mods: ModifierContributionSum, spent: Set<string> | undefined, loop?: EventLoop): ResolvedStep[] {
  for (const { carrier, key } of mods.consumedResourceCaps) spent?.add(spentKey(carrier, resourceCapKey(key)));
  return capUseSteps(mods.consumedResourceCaps, loop);
}

/** Named args for {@link rollSelfCheck}. */
export type RollSelfCheckArgs = {
  /** Absent: a flat roll with nothing folded (direct table fixtures). Every production caller passes one. */
  ctx: SelfCheckContext | undefined;
  dice: DiceSource;
  /** The roller, for its id and name. Its standing modifiers are read off `ctx.state`, not this snapshot. */
  combatant: Combatant;
  /** The stat rolled (`"hull"`, `"engineering"`, …), recorded as `against` and matched against `saveScope.stat`. */
  against: string;
  /** The flat bonus the site already computes (`saveBonusFor`, or `base.systems`). */
  bonus: number;
  dc: number;
  /** The roll's cause, matched against `saveScope.cause` (Stable Structure's prone/knockback). */
  cause?: string;
  /** A save or a skill check, matched against `saveScope.roll`. */
  roll: SaveCheckRoll;
};

export type SelfCheckRoll = {
  save: SaveStep;
  /** The `save` step, then a `status_remove` for each consumed status (Bolster). */
  steps: ResolvedStep[];
  warnings: string[];
};

/**
 * Roll a carrier-self save or check: `d20 + bonus` plus the folded accuracy dice, vs `dc`. An
 * auto-failed stat (Stunned: HULL, AGILITY; RAW p.78) draws no die and records `autoFailed`. A
 * consumed-on-use boon (Bolster) is removed after it rides a roll, and recorded in `ctx.spent`.
 */
export function rollSelfCheck(args: RollSelfCheckArgs): SelfCheckRoll {
  const { ctx, dice, combatant: c, against, bonus, dc, cause, roll } = args;
  const mods = ctx === undefined ? undefined : foldSelfSaveCheck({ state: ctx.state, carrierId: c.id, overlay: ctx.overlay, scope: { stat: against, cause, roll }, spent: ctx.spent });
  if (mods !== undefined && mods.autoFailChecks.includes(against)) {
    const save: SaveStep = { op: "save", actor: c.id, against, d20: 0, accuracy: 0, bonus, total: bonus, dc, result: "fail", autoFailed: true };
    return { save, steps: [save], warnings: [`${c.name}: ${against.toUpperCase()} auto-fails.`] };
  }
  const acc = mods?.saveCheckBonus ?? 0;
  const contributions = mods?.saveCheckContributions ?? [];
  const rollOnce = (): CheckRoll<SaveStep> => {
    const { result: d, id } = drawRoll({
      loop: ctx?.loop,
      dice,
      roll: { kind: "roll", roller: c.id, purpose: { kind: roll, stat: against, ...(cause !== undefined ? { cause } : {}) }, spec: { dice: [{ count: 1, sides: 20 }], accuracy: acc } },
      draw: (src) => rollD20(src, acc),
      facts: (r) => ({ accuracyMod: r.accuracyMod ?? 0 }),
    });
    const d20 = d.draws[0]!.value;
    const accuracy = d.accuracyMod ?? 0;
    const total = d20 + bonus + accuracy;
    const save: SaveStep = {
      op: "save",
      actor: c.id,
      against,
      d20,
      accuracy,
      ...(contributions.length > 0 ? { accuracyContributions: [...contributions] } : {}),
      bonus,
      total,
      dc,
      result: total >= dc ? "pass" : "fail",
    };
    return { roll: save, ...(id !== undefined ? { id } : {}), total, failed: save.result === "fail" };
  };
  const warnings: string[] = [];
  const first = rollOnce();
  const { standing, spend } = settleCheck({
    ctx,
    dice,
    roller: c,
    purpose: { kind: roll, stat: against, ...(cause !== undefined ? { cause } : {}) },
    dc,
    first,
    reroll: rollOnce,
    label: (cur) => `${against.toUpperCase()} ${roll} failed (${cur.total} vs ${dc})`,
  });
  const save = standing.roll;
  if (standing !== first) warnings.push(`${c.name}: rerolls the ${against.toUpperCase()} ${roll} (${first.total} → ${save.total}).`);
  const steps: ResolvedStep[] = [save, ...spend];
  if (ctx !== undefined && mods !== undefined) steps.push(...spendFoldedCaps(mods, ctx.spent, ctx.loop));
  if (ctx !== undefined && mods !== undefined && acc !== 0) {
    steps.push(...spendConsumedStatuses({ carrier: ctx.state.combatants[c.id] ?? { id: c.id, statuses: [] }, statuses: mods.consumeOnSaveCheck, state: ctx.state, loop: ctx.loop }));
    for (const status of mods.consumeOnSaveCheck) {
      ctx.spent.add(spentKey(c.id, status));
      warnings.push(`${c.name}: ${status} grants ${acc > 0 ? "+" : ""}${acc}◆ to this ${against} roll, then is consumed.`);
    }
  }
  return { save, steps, warnings };
}
